import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShieldAlt,
    faSearch,
    faBalanceScale,
    faCogs,
    faTasks,
    faChartLine,
    faFileInvoiceDollar
} from '@fortawesome/free-solid-svg-icons';
import AOS from 'aos';
import 'aos/dist/aos.css';

const services = [
    { icon: faFileInvoiceDollar, title: 'VAT Registration', description: 'Assistance with VAT registration and obtaining your Tax Registration Number (TRN).' },
    { icon: faBalanceScale, title: 'VAT Return Filing', description: 'Accurate and timely preparation and submission of VAT returns to the FTA.', color: 'text-success' },
    { icon: faSearch, title: 'VAT Impact Assessment', description: 'Analyzing the potential impact of VAT on your business operations and finances.', color: 'text-warning' },
    { icon: faCogs, title: 'VAT Implementation Support', description: 'Guiding you through the process of implementing VAT-compliant systems and processes.', color: 'text-info' },
    { icon: faShieldAlt, title: 'VAT Compliance Audits', description: 'Internal audits to ensure ongoing compliance and identify any potential risks.', color: 'text-danger' },
    { icon: faChartLine, title: 'VAT Advisory', description: 'Providing expert advice on complex VAT matters and specific business scenarios.' },
];

const reasons = [
    { title: 'Specialized Expertise', description: 'Our consultants have extensive knowledge of the UAE VAT law and its practical application.' },
    { title: 'Risk Mitigation', description: 'We help you identify and mitigate potential VAT-related risks to avoid penalties.' },
    { title: 'Customized Solutions', description: 'We tailor our services to your business model, size, and industry for effective results.' },
    { title: 'Efficiency & Accuracy', description: 'We ensure your VAT processes are efficient and your returns are filed with precision.' },
    { title: 'Strategic Guidance', description: 'We offer strategic advice to help you manage your VAT obligations and optimize cash flow.' },
    { title: 'Dedicated Support', description: 'Receive continuous support from a team of professionals dedicated to your business’s success.' },
];

const VATConsulting = () => {
    useEffect(() => {
        AOS.init({ duration: 1000, once: true });
    }, []);

    const sectionStyles = {
        padding: '80px 0',
        fontFamily: 'Segoe UI, sans-serif'
    };

    const leadStyle = {
        color: '#6c757d',
        fontSize: '1.25rem'
    };

    const cardBaseStyle = {
        borderRadius: '15px',
        transition: 'all 0.3s ease',
        height: '100%',
        cursor: 'pointer'
    };

    const handleMouseEnter = (e) => {
        e.currentTarget.style.backgroundColor = '#7f8986ff';
        e.currentTarget.style.color = '#fff';
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
    };

    const handleMouseLeave = (e) => {
        e.currentTarget.style.backgroundColor = '#fff';
        e.currentTarget.style.color = '#000';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
    };

    return (
        <>
            {/* Hero Section */}
            <section
                style={{
                    position: "relative",
                    height: "90vh",
                    backgroundImage: `url('./em2.png')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    textAlign: "center"
                }}
            >
                <div style={{ background: "rgba(0,0,0,0.4)", padding: "20px", borderRadius: "10px" }}>
                    <p style={{ fontSize: "1.2rem" }}>Navigate the complexities of VAT with confidence</p>
                    <h1 style={{ fontWeight: "bold", fontSize: "2.5rem" }}>Expert VAT solutions for seamless compliance and business growth</h1>
                    
                    {/* WhatsApp Button */}
                    <a
                        href="https://wa.me/+971588124755"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whatsapp-btn"
                    >
                        <i className="bi bi-whatsapp me-2"></i> Chat on WhatsApp
                    </a>
                </div>
            </section>

            <main>
                {/* Intro Section */}
                <section style={{ ...sectionStyles, backgroundColor: '#fff' }}>
                    <Container>
                        <p style={leadStyle} data-aos="fade-up">
                            At <strong>Emkan</strong>, We are aware that one of the most important parts of conducting business in the United Arab Emirates is adhering to the Value Added Tax (VAT). Since its introduction in 2018, VAT has grown to be a crucial component of financial operations; nonetheless, many firms still struggle with it because of its regular changes, extensive reporting requirements, and severe fines for mistakes or delays. In addition to preventing penalties, proper VAT management is necessary to provide seamless corporate operations and financial transparency.

                            <details>
                                <summary>Read More</summary>
                                <p>
Our VAT consulting services are designed to help companies of all sizes manage their VAT obligations with confidence and effectiveness. We provide a full range of services tailored to your business's needs, including compliance checks, advice, and VAT registration and return filing. Whether you are a SME struggling with submission, a new company seeking VAT assistance, or a larger organization managing intricate transactions, our team ensures that you remain fully compliant with Federal Tax Authority (FTA) standards.

We at Emkan do more than just prepare tax returns. In order to detect risks, optimize tax positions, and highlight potential for input VAT collection or savings, our skilled VAT advisors examine your contracts, transactions, and invoicing procedures. Additionally, we help you structure your business activities to reduce VAT exposure while maintaining legal compliance.

We support aggressive VAT administration. This means that we help you prepare ahead of time with accurate record-keeping, frequent compliance checks, and timely updates on regulatory changes—we don't only aid you when it comes time to file. There won't be any surprises during audits or inspections thanks to this proactive strategy.

Since every company is unique, our VAT solutions are tailored to each one. After taking the time to learn about your business and activities, we develop strategies that meet your compliance requirements and financial objectives.

By selecting Emkan, you acquire a reliable partner who simplifies VAT, ensures compliance, and frees you up to concentrate on managing and expanding your company.
                                </p>
                            </details>
                        </p>
                    </Container>
                </section>

                {/* Services Section */}
                <section style={{ ...sectionStyles, backgroundColor: '#7d94a6ff' }}>
                    <Container>
                        <h2 className="text-white fw-bold text-center mb-5" data-aos="fade-up">Our VAT Consulting Services</h2>
                        <Row className="g-4">
                            {services.map((service, index) => (
                                <Col md={4} key={index} data-aos="zoom-in" data-aos-delay={index * 100}>
                                    <Card
                                        style={cardBaseStyle}
                                        className="p-4 text-center"
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <Card.Body>
                                            <FontAwesomeIcon
                                                icon={service.icon}
                                                className={`fs-1 mb-3 ${service.color || 'text-primary'}`}
                                            />
                                            <h5 className="fw-bold">{service.title}</h5>
                                            <p>{service.description}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>

                {/* Why Choose Us Section */}
                <section style={{ ...sectionStyles, backgroundColor: '#7d94a6ff' }}>
                    <Container>
                        <h2 className="text-white fw-bold text-center mb-5" data-aos="fade-up">Why Choose Emkan for VAT Consulting?</h2>
                        <Row className="g-4">
                            {reasons.map((reason, index) => (
                                <Col md={4} key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                                    <Card
                                        style={cardBaseStyle}
                                        className="p-4"
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <Card.Body>
                                            <h5 className="fw-bold">{reason.title}</h5>
                                            <p>{reason.description}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>
            </main>
        </>
    );
};

export default VATConsulting;